import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { keyframes } from "@mui/system";
import { useTranslationApp } from "../../lib/i18next";
import { CHAT_THINKING } from "../../i18n/keysTranslations";
import CircularLoader from "../Loaders/CircularLoader";

// Animación shimmer que se mueve de derecha a izquierda
const shimmer = keyframes`
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: 0% 0;
  }
`;

/** Shared across reasoning indicator mounts so loader growth stays continuous. */
let sharedReasoningLoaderProgress = 0;

// eslint-disable-next-line
export const resetChatReasoningLoaderProgress = () => {
  sharedReasoningLoaderProgress = 0;
};

const ChatReasoningIndicator = ({ messageKeys = [CHAT_THINKING] }) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [initialLoaderProgress] = useState(() => sharedReasoningLoaderProgress);
  const theme = useTheme();
  const { t } = useTranslationApp();

  // eslint-disable-next-line
  const reasoningMessageKeys =
    messageKeys.length > 0 ? messageKeys : [CHAT_THINKING];

  const onLoaderProgressChange = (progress) => {
    sharedReasoningLoaderProgress = progress;
  };

  // Cycle through reasoning messages in order, stop at the last one
  useEffect(() => {
    const messageInterval = setInterval(() => {
      setCurrentMessageIndex((prev) => {
        if (prev >= reasoningMessageKeys.length - 1) {
          return prev;
        }
        return prev + 1;
      });
    }, 4000);

    return () => clearInterval(messageInterval);
  }, [reasoningMessageKeys.length]);

  // Keep text index in range when message keys change (e.g. analyzing → thinking)
  useEffect(() => {
    setCurrentMessageIndex((prev) =>
      Math.min(prev, reasoningMessageKeys.length - 1)
    );
  }, [reasoningMessageKeys]);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        backgroundColor: "transparent",
        borderRadius: 3,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularLoader
          {...(initialLoaderProgress > 0
            ? { progress: initialLoaderProgress }
            : {})}
          onProgressChange={onLoaderProgressChange}
          size={20}
          time={60000}
        />
      </Box>

      <Typography
        variant="body2"
        component="span"
        sx={{
          display: "flex",
          alignItems: "center",
          background: `linear-gradient(90deg, ${theme.palette.text.secondary} 0%, ${theme.palette.text.secondary} 40%, rgba(255, 255, 255, 0.8) 50%, ${theme.palette.text.secondary} 60%, ${theme.palette.text.secondary} 100%, ${theme.palette.text.secondary} 100%, ${theme.palette.text.secondary} 140%, rgba(255, 255, 255, 0.8) 150%, ${theme.palette.text.secondary} 160%, ${theme.palette.text.secondary} 200%)`,
          backgroundSize: "200% 100%",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
          animation: `${shimmer} 1.4s linear infinite`,
          fontWeight: 500,
          fontSize: "0.9rem",
          minHeight: "20px",
        }}
      >
        {t(reasoningMessageKeys[currentMessageIndex])}
      </Typography>
    </Box>
  );
};

export default ChatReasoningIndicator;
